import { ArrowRight, Check, Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { IMAGES, SIX_ELEMENTS, CYCLE_STEPS } from "../lib/content";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { CtaBand } from "./Home";
import { PageHero } from "../components/PageHero";

// Distinct, contrasting accent per element for a vivid, animated grid
const PAIR_COLORS = ["#FD6513", "#1F7A6B", "#B4791F", "#7A3B5E", "#2F4B7C", "#3E6B3A"];

export default function CoachingPairs() {
  const [openEl, setOpenEl] = useState(null);
  return (
    <div data-testid="coaching-pairs-page">
      <PageHero
        eyebrow="The Coaching Pairs Method"
        title="Helping each other grow as leaders."
        intro="Leaders choosing a coaching partner from another function, swapping every six months – building the cross-functional trust that turns a group of directors into one team."
        image={IMAGES.coachingPairs}
      />

      {/* WHY */}
      <section className="py-24 md:py-32 bg-ivory">
        <div className="container-px grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <SectionHeading
              overline="Why Coaching Pairs"
              title="The glue that holds an organisation together."
            />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 space-y-5 text-lg text-ink/80 font-light leading-relaxed">
            <p>
              These small{" "}
              <span className="text-windsor font-medium">bubbles of high-trust working</span> are part
              of the glue that holds an organisation together – through ever stronger cross-team
              loyalties and a Board-led commitment to the Tough Trust Method.
            </p>
            <p>
              It signals to all staff that the Board promotes a voluntary, high-trust culture –
              embedded through Coaching Pairs across everyone in a leadership position.
            </p>
            <p>
              And high-trust working is underpinned by tough trust: the kind that will always trip up
              anyone who tries to take advantage of others' trust in them, at the expense of the
              organisation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TEAMS AT WINDSOR */}
      <section className="pb-24 bg-ivory" data-testid="pairs-windsor-images">
        <div className="container-px grid sm:grid-cols-2 gap-6 items-start">
          <img
            src={IMAGES.westSteps1}
            alt="Pete with a leadership team on the West Steps of St George's Chapel, Windsor Castle"
            loading="lazy"
            className="w-full h-auto border border-ink/10"
          />
          <img
            src={IMAGES.westSteps2}
            alt="Pete with a leadership team on the West Steps of St George's Chapel, Windsor Castle"
            loading="lazy"
            className="w-full h-auto border border-ink/10"
          />
        </div>
        <div className="container-px">
          <p className="text-ink/55 text-sm mt-4 italic">
            Pete with leadership teams on the West Steps of St George's Chapel, Windsor Castle.
          </p>
        </div>
      </section>

      {/* SIX ELEMENTS */}
      <section className="relative py-24 md:py-32 bg-ivory overflow-hidden" data-testid="six-elements">
        <div className="container-px relative z-10">
          <Reveal>
            <SectionHeading
              overline="The six elements"
              title="What leaders do when they pair up"
              intro="Coaching Pairs bring leadership and coaching together as one. Six simple commitments make it work."
            />
          </Reveal>
          <p className="text-center text-ink/50 text-sm mt-6" data-testid="six-elements-hint">
            Tap any element to see it in action.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {SIX_ELEMENTS.map((el, i) => {
              const c = PAIR_COLORS[i % PAIR_COLORS.length];
              const isOpen = openEl === el.n;
              return (
                <motion.button
                  key={el.n}
                  type="button"
                  onClick={() => setOpenEl(isOpen ? null : el.n)}
                  data-testid={`pairs-element-${el.n}`}
                  aria-expanded={isOpen}
                  initial={{ opacity: 0, y: 45, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  style={{ "--pc": c, backgroundColor: isOpen ? c : "#ffffff" }}
                  className={`group relative h-full text-left p-8 rounded-sm border overflow-hidden transition-all duration-500 hover:-translate-y-2 ${
                    isOpen
                      ? "border-transparent shadow-2xl"
                      : "border-ink/10 shadow-lg shadow-ink/5 hover:shadow-2xl"
                  }`}
                >
                  {/* top colour bar */}
                  <span
                    className="absolute top-0 left-0 h-1.5 w-full"
                    style={{ backgroundColor: isOpen ? "rgba(255,255,255,0.35)" : c }}
                    aria-hidden
                  />
                  <div className="relative z-10">
                    <div className="flex items-start justify-between gap-3">
                      <span
                        className="w-16 h-16 grid place-items-center rounded-full font-serif text-3xl text-white transition-transform duration-300 group-hover:scale-110"
                        style={{ backgroundColor: isOpen ? "rgba(255,255,255,0.2)" : c }}
                      >
                        {el.n}
                      </span>
                      <span
                        className={`mt-2 w-8 h-8 grid place-items-center rounded-full border transition-colors ${
                          isOpen ? "border-white/50 text-white" : "border-ink/20 text-ink/80"
                        }`}
                        aria-hidden
                      >
                        {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                      </span>
                    </div>
                    <h3
                      className={`font-serif text-2xl mt-6 leading-tight transition-colors duration-300 ${
                        isOpen ? "text-white" : "text-ink"
                      }`}
                    >
                      {el.title}
                    </h3>
                    <span
                      className="block h-0.5 w-10 mt-3 transition-all duration-500 group-hover:w-24"
                      style={{ backgroundColor: isOpen ? "rgba(255,255,255,0.6)" : c }}
                    />
                    <p
                      className={`font-light leading-relaxed mt-4 transition-colors duration-300 ${
                        isOpen ? "text-white/90" : "text-ink/85"
                      }`}
                    >
                      {el.body}
                    </p>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="mt-5 pt-5 border-t border-white/25">
                            <p className="uppercase text-xs tracking-[0.22em] font-medium text-white/70">
                              In action
                            </p>
                            <p className="text-white font-light leading-relaxed mt-2 italic">
                              {el.example}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE CYCLE */}
      <section className="py-24 md:py-32 bg-cloud">
        <div className="container-px">
          <Reveal>
            <SectionHeading
              overline="The six-month cycle"
              title="A rhythm that becomes self-sustaining"
              intro="Once your second round is underway, Coaching Pairs become a natural part of how your team works together – no drama, no gimmicks."
            />
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
            {CYCLE_STEPS.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border-t-2 border-windsor pt-6 h-full"
              >
                <h3 className="font-serif text-2xl text-ink">{step.title}</h3>
                <p className="text-ink/85 font-light leading-relaxed mt-3">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SUPPORT / WORKSHOP */}
      <section className="py-24 md:py-32 bg-ivory">
        <div className="container-px grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-6">
            <p className="overline">Getting started</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight mt-4">
              An initial workshop sets your team up to thrive.
            </h2>
            <p className="text-ink/80 text-lg font-light leading-relaxed mt-6">
              The ideal way in is for the CEO to introduce Coaching Pairs to their extended
              leadership team in a single in-person workshop – establishing cross-team coaching as a
              core part of how you lead.
            </p>
            <div className="mt-8 space-y-3">
              {[
                "Explore the six key elements together",
                "Invite members to form their first Coaching Pair",
                "Run a first 30–40 minute pairing session on the day",
                "Set the date for your six-month awayday",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <Check size={20} className="text-windsor mt-1 shrink-0" />
                  <p className="text-ink/85 font-light">{item}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-6">
            <div className="bg-white border border-ink/10 p-9">
              <h3 className="font-serif text-2xl text-ink">Two ways Pete can help</h3>
              <div className="mt-7 space-y-7">
                <div>
                  <p className="font-medium text-ink">Background coaching support</p>
                  <p className="text-ink/85 font-light leading-relaxed mt-2">
                    Prefer to facilitate the workshop yourself? Pete offers light-touch support
                    behind the scenes – a couple of short Teams sessions as you introduce Coaching
                    Pairs to your team.
                  </p>
                </div>
                <div className="border-t border-ink/10 pt-7">
                  <p className="font-medium text-ink">Full facilitation</p>
                  <p className="text-ink/85 font-light leading-relaxed mt-2">
                    Or have Pete facilitate the introductory workshop for you. Once Coaching Pairs are
                    up and running, they cost you very little – in a league of their own compared with
                    ongoing external coaching fees.
                  </p>
                </div>
              </div>
              <p className="text-ink/85 font-light mt-7">
                Get in touch if you'd like to explore either option.
              </p>
              <a
                href="mailto:pete@peteashbycoaching.com"
                data-testid="pairs-cta-contact"
                className="inline-flex items-center gap-2 mt-6 bg-[#C2410C] text-ivory px-7 py-3.5 font-medium hover:bg-windsor-dark transition-colors"
              >
                Discuss introducing Coaching Pairs <ArrowRight size={17} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
